import { z } from "zod";
import { idSchema, instantSchema, moneySchema } from "@amber/contracts";

const textSchema = z.string().min(1);

export const CONFIDENCE_TARGETS = {
  name: "speaker-name-correct@1",
  profile: "profile-match-given-person@1",
  voice: "turn-local-voice-correct@1",
  attribution: "contribution-attribution@1",
} as const;

export const confidenceRecordSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("model_estimate"),
    label: z.literal("model estimate"),
    value: z.number().min(0).max(1),
    target: textSchema,
    method: textSchema,
    calibration: z.literal("uncalibrated"),
  }),
  z.strictObject({
    kind: z.literal("unavailable"),
    label: z.literal("unavailable"),
    value: z.null(),
    target: textSchema,
    method: textSchema,
    calibration: z.literal("unavailable"),
    unavailable_reason: textSchema,
  }),
]);
export type ConfidenceRecord = z.infer<typeof confidenceRecordSchema>;

export const mentionSchema = z.strictObject({ evidence: textSchema, text: textSchema });

export const namingReadSchema = z
  .strictObject({
    id: idSchema("nmd"),
    status: z.enum(["assigned", "ambiguous", "unresolved"]),
    name: textSchema.nullable(),
    mentions: z.array(mentionSchema),
    confidence: confidenceRecordSchema.nullable(),
    evidence: z.array(textSchema),
    alternatives: z.array(
      z.strictObject({ name: textSchema, confidence: z.number().min(0).max(1), evidence: z.array(textSchema) }),
    ),
    candidate_names: z.array(textSchema),
    reason: textSchema.nullable(),
    flags: z.array(textSchema),
    method: textSchema,
  })
  .refine((n) => (n.status === "assigned") === (n.name !== null), "only an assigned decision has a name")
  .refine(
    (n) => (n.status === "assigned") === (n.confidence?.kind === "model_estimate"),
    "an assigned name has a score; other outcomes have none",
  )
  .refine((n) => n.status !== "assigned" || n.evidence.length > 0, "an assigned name cites evidence")
  .refine((n) => n.status === "assigned" || n.reason !== null, "an unassigned outcome has a reason");
export type NamingRead = z.infer<typeof namingReadSchema>;

export const profileReadSchema = z
  .strictObject({
    id: idSchema("pmd"),
    decision: z.enum(["matched", "ambiguous", "no_match", "not_applicable"]),
    profile_id: idSchema("prf").nullable(),
    display_name: textSchema.nullable(),
    confidence: confidenceRecordSchema.nullable(),
    candidates: z.array(
      z.strictObject({ profile_id: idSchema("prf"), display_name: textSchema, confidence: z.number().min(0).max(1) }),
    ),
    evidence: z.array(textSchema),
    reason: textSchema.nullable(),
    method: textSchema,
  })
  .refine((p) => (p.decision === "matched") === (p.profile_id !== null), "only a matched decision names a profile")
  .refine(
    (p) => (p.decision === "matched") === (p.confidence?.kind === "model_estimate"),
    "only a matched decision has a score",
  )
  .refine((p) => p.decision !== "ambiguous" || p.candidates.length >= 2, "an ambiguous match lists its candidates");
export type ProfileRead = z.infer<typeof profileReadSchema>;

export const lowestAttributionSchema = z.strictObject({
  value: z.number().min(0).max(1).nullable(),
  unknown: z.boolean(),
  label: z.literal("model estimate"),
});

export const speakerReadSchema = z.strictObject({
  id: idSchema("spk"),
  label: textSchema,
  provider_labels: z.array(textSchema),
  flags: z.array(textSchema),
  role: z.strictObject({ value: textSchema, inferred: z.boolean() }).nullable(),
  naming: namingReadSchema.nullable(),
  profile: profileReadSchema.nullable(),
  summary: z.strictObject({
    speaking_ms: z.number().int().nonnegative(),
    turn_count: z.number().int().nonnegative(),
    lowest_attribution: lowestAttributionSchema,
  }),
});
export type SpeakerRead = z.infer<typeof speakerReadSchema>;

export const textLocatorSchema = z.strictObject({
  scheme: z.enum(["paragraph", "page", "post"]),
  anchor: z.string().min(1),
  paragraphs: z.tuple([z.number().int().positive(), z.number().int().positive()]),
  column: z.string().nullable(),
  page: z.number().int().positive().nullable(),
  contribution_id: z.string().nullable(),
  section: z.string().nullable(),
});
export type TextLocator = z.infer<typeof textLocatorSchema>;

export const textDocumentSchema = z.strictObject({
  kind: z.enum(["official_record", "x_thread", "instagram_post", "html", "pdf", "news"]),
  section_path: z.array(z.string()),
  links: z.array(
    z.strictObject({
      kind: z.enum(["previous", "next", "media", "parent"]),
      title: z.string().nullable(),
      url: z.string(),
    }),
  ),
  blocks: z.array(
    z.strictObject({
      anchor: z.string(),
      page: z.number().int().positive().nullable(),
      column: z.string().nullable(),
      speaker: z.string().nullable(),
      text: z.string(),
      procedural: z.boolean(),
      turn: z.number().int().nonnegative().nullable(),
    }),
  ),
});
export type TextDocument = z.infer<typeof textDocumentSchema>;

export const turnReadSchema = z.strictObject({
  idx: z.number().int().nonnegative(),
  speaker_id: idSchema("spk").nullable(),
  first_word: z.number().int().nonnegative(),
  last_word: z.number().int().nonnegative(),
  start_ms: z.number().int().nonnegative(),
  end_ms: z.number().int().positive(),
  language: z.string().nullable(),
  flags: z.array(textSchema),
  voice: confidenceRecordSchema,
  attribution: z
    .strictObject({ to_name: confidenceRecordSchema, to_profile: confidenceRecordSchema.nullable() })
    .nullable(),
  label: z.string().nullable().optional(),
  locator: textLocatorSchema.nullable().optional(),
});
export type TurnRead = z.infer<typeof turnReadSchema>;

export const identificationReadSchema = z.strictObject({
  id: idSchema("idn"),
  state: z.enum(["running", "completed", "failed"]),
  provider: textSchema,
  model: textSchema,
  adapter_version: textSchema,
  methods: z.record(z.string(), textSchema),
  cost: moneySchema.nullable(),
  failure: z.strictObject({ outcome: textSchema, detail: textSchema }).nullable(),
  started_at: instantSchema,
  finished_at: instantSchema.nullable(),
});

export const speakersReadSchema = z.strictObject({
  result_id: idSchema("res"),
  identification: identificationReadSchema.nullable(),
  speakers: z.array(speakerReadSchema),
  turns: z.array(turnReadSchema),
});
export type SpeakersRead = z.infer<typeof speakersReadSchema>;
